import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Disclaimer } from "@/components/Disclaimer";
import { useAuth } from "@/context/AuthContext";
import { haptic } from "@/lib/haptics";
import api, { formatApiErrorDetail } from "@/lib/api";

const PER_PAGE = 6;

export default function Assessment() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [likert, setLikert] = useState([]);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/assessments/${slug}/questions`).then(({ data }) => {
      setMeta(data); setQuestions(data.questions); setLikert(data.likert);
    }).catch(() => { toast.error("Assessment not found"); navigate("/"); });
  }, [slug]); // eslint-disable-line

  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < questions.length; i += PER_PAGE) out.push(questions.slice(i, i + PER_PAGE));
    return out;
  }, [questions]);

  const needsEmail = !user;
  const isEmailStep = page >= pages.length;
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;
  const currentPage = pages[page];
  const pageComplete = currentPage ? currentPage.every((q) => answers[q.id]) : true;
  const isLastPage = page === pages.length - 1;

  const setAnswer = (qid, value) => { haptic(10); setAnswers((a) => ({ ...a, [qid]: value })); };

  const next = () => {
    if (!pageComplete) { toast.error("Please answer every question to continue."); return; }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setPage((p) => p + 1);
  };
  const back = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setPage((p) => Math.max(0, p - 1)); };

  const touch = React.useRef(null);
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.5) return; // ignore taps / vertical scrolls
    if (dx < 0) {
      if (isEmailStep) return;
      if (pageComplete) { haptic(12); next(); } else { toast.error("Answer every question to continue."); }
    } else {
      if (page > 0) { haptic(12); back(); }
    }
  };

  const submit = async () => {
    if (needsEmail && !email) { toast.error("Enter your email to see your results."); return; }
    setSubmitting(true);
    try {
      const payload = { answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })) };
      if (needsEmail) { payload.email = email; payload.name = name || undefined; }
      const { data } = await api.post(`/assessments/${slug}/submit`, payload);
      if (data.user) setUser(data.user);
      navigate(`/results/${data.result_id}`, { state: { result: data.result, temp_password: data.temp_password, new_account: data.new_account } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
      setSubmitting(false);
    }
  };

  if (!meta || questions.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950" data-testid="assessment-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" />
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-600">Preparing your assessment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="safe-top fixed top-0 left-0 right-0 z-40 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-5 py-3.5 sm:px-8">
          <div className="flex items-center justify-between">
            <Logo />
            <span className="font-mono text-xs text-slate-500" data-testid="progress-label">{progress}%</span>
          </div>
          <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-slate-800">
            <motion.div className="h-full rounded-full" style={{ background: meta.color }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} data-testid="progress-bar" />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 pb-32 pt-32 sm:px-8" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <AnimatePresence mode="wait">
          {!isEmailStep && currentPage && (
            <motion.div key={`page-${page}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: meta.color }}>{meta.name}</p>
              <h1 className="mt-3 font-heading text-2xl font-light tracking-tight text-slate-50 sm:text-3xl">How true is each of these for you?</h1>
              <p className="mt-2 text-sm text-slate-500">There are no right answers — only your truth. ({page + 1} of {pages.length})<span className="ml-1.5 text-slate-600 sm:hidden">· swipe to navigate</span></p>
              <div className="mt-8 space-y-6">
                {currentPage.map((q, qi) => (
                  <div key={q.id} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 sm:p-6" data-testid={`question-${q.id}`}>
                    <p className="text-base leading-relaxed text-slate-200"><span className="mr-2 font-mono text-xs text-slate-600">{String(page * PER_PAGE + qi + 1).padStart(2, "0")}</span>{q.text}</p>
                    <div className="mt-5 grid grid-cols-5 gap-1.5 sm:gap-2">
                      {likert.map((opt) => {
                        const active = answers[q.id] === opt.value;
                        return (
                          <button key={opt.value} onClick={() => setAnswer(q.id, opt.value)} className={`flex min-h-[68px] flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-2.5 text-center transition-all duration-200 active:scale-95 ${active ? "border-teal-400 bg-teal-500/15 scale-[1.03]" : "border-slate-800 bg-slate-950 hover:border-slate-600"}`} data-testid={`answer-${q.id}-${opt.value}`} title={opt.label}>
                            <span className={`rounded-full transition-all ${active ? "h-3.5 w-3.5 bg-teal-400" : "h-3 w-3 bg-slate-700"}`} />
                            <span className={`text-[10px] leading-tight sm:text-[11px] ${active ? "text-teal-300" : "text-slate-500"}`}>{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {isEmailStep && (
            <motion.div key="email-step" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.35 }}>
              <CheckCircle size={48} weight="duotone" style={{ color: meta.color }} />
              <h1 className="mt-5 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-4xl">Your result is ready.</h1>
              <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-400">Where should we send your free report? Enter your email to reveal your result — and we'll create a free account so you can revisit it anytime.</p>
              <div className="mt-8 max-w-md space-y-4">
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40" data-testid="capture-name-input" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40" data-testid="capture-email-input" />
              </div>
              <div className="mt-6 max-w-md"><Disclaimer compact /></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-800/60 bg-slate-950/85 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 sm:px-8">
          <button onClick={back} disabled={page === 0} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors hover:text-slate-200 disabled:opacity-30" data-testid="assessment-back-btn"><ArrowLeft size={16} /> Back</button>
          {isEmailStep ? (
            <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-slate-950 transition-all disabled:opacity-50" style={{ background: meta.color }} data-testid="assessment-submit-btn">
              {submitting ? "Building your result…" : "Reveal My Result"} {!submitting && <ArrowRight size={16} weight="bold" />}
            </button>
          ) : (
            <button onClick={isLastPage && !needsEmail ? submit : next} disabled={!pageComplete || submitting} className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-slate-950 transition-all disabled:cursor-not-allowed disabled:opacity-40" style={{ background: meta.color }} data-testid="assessment-next-btn">
              {isLastPage && !needsEmail ? (submitting ? "Building…" : "Reveal My Result") : "Continue"} {!submitting && <ArrowRight size={16} weight="bold" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
